import React from "react";
import { motion } from "framer-motion";
import { FaCertificate, FaCheckCircle, FaCalendarAlt } from "react-icons/fa";

// Import certification logos
import oscpLogo from "../../assets/images/oscp.png";
import cksLogo from "../../assets/images/cks.png";
import ckaLogo from "../../assets/images/cka.png";

const certifications = [
  {
    id: 1,
    name: "OSCP - Offensive Security Certified Professional",
    issuer: "Offensive Security",
    date: "2022",
    logo: oscpLogo,
    skills: ["Penetration Testing", "Exploit Development", "Security Research"],
    highlight: true,
  },
  {
    id: 2,
    name: "CKS - Certified Kubernetes Security Specialist",
    issuer: "Cloud Native Computing Foundation",
    date: "2021",
    logo: cksLogo,
    skills: ["Kubernetes Security", "Container Security", "Cloud Native"],
  },
  {
    id: 3,
    name: "CKA - Certified Kubernetes Administrator",
    issuer: "Cloud Native Computing Foundation",
    date: "2021",
    logo: ckaLogo,
    skills: ["Kubernetes", "Container Orchestration", "DevOps"],
  },
];

const CertificationsSection = () => {
  return (
    <section className="mb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Professional Certifications
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
              className={`relative overflow-hidden rounded-xl ${
                cert.highlight
                  ? "bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-2 border-yellow-400/50"
                  : "bg-white/5 border border-white/10"
              } backdrop-blur-sm p-6 hover:bg-white/10 transition-all duration-300 group`}
            >
              {cert.highlight && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  ADVANCED
                </div>
              )}

              <div className="flex items-start gap-4">
                {/* Logo/Icon */}
                <div className="w-16 h-16 bg-white/10 rounded-lg p-2 flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  {cert.logo ? (
                    <img
                      src={cert.logo}
                      alt={cert.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <FaCertificate className="text-2xl text-yellow-400" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors">
                    {cert.name}
                  </h3>

                  <p className="text-sm text-gray-400 mb-2">{cert.issuer}</p>

                  <div className="flex items-center text-xs text-gray-500 mb-3">
                    <FaCalendarAlt className="mr-1" />
                    {cert.date}
                  </div>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-1">
                    {cert.skills.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Verified Badge */}
              <div className="absolute bottom-2 right-2 text-green-400 opacity-50 group-hover:opacity-100 transition-opacity">
                <FaCheckCircle className="text-xl" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto"
        >
          {[
            {
              label: "Total Certifications",
              value: "3",
              color: "from-blue-500/20 to-blue-600/20",
            },
            {
              label: "Cloud Certifications",
              value: "2",
              color: "from-purple-500/20 to-purple-600/20",
            },
            {
              label: "Years of Experience",
              value: "10+",
              color: "from-orange-500/20 to-orange-600/20",
            },
          ].map((stat, index) => (
            <div
              key={stat.label}
              className={`bg-gradient-to-br ${stat.color} backdrop-blur-sm border border-white/10 rounded-lg p-4 text-center hover:scale-105 transition-transform duration-300`}
            >
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default CertificationsSection;
